import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Hero from '../components/Hero';
import ProductCard from '../components/ProductCard';
import { ArrowRight, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { imageFallback, resolveImageUrl } from '../lib/imageUtils';

const HomePage = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/products?featured=true&limit=4`),
        axios.get(`${BACKEND_URL}/api/categories`)
      ]);
      setFeaturedProducts(productsRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Hero />

      <section className="py-24 px-6 md:px-12" data-testid="categories-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 space-y-4">
            <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Our Collections</p>
            <h2 className="text-4xl sm:text-5xl font-light tracking-tighter text-white">
              Shop by Category
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.map((category) => (
              <Link
                key={category.id}
                to={`/products?category=${category.id}`}
                className="group relative overflow-hidden aspect-[3/4] bg-[#141414]"
                data-testid={`category-card-${category.id}`}
              >
                <img
                  src={resolveImageUrl(category.image, BACKEND_URL)}
                  alt={category.name}
                  onError={imageFallback}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-8">
                  <h3 className="text-2xl font-light text-white mb-2">{category.name}</h3>
                  <p className="text-sm text-[#A3A3A3] mb-4">{category.description}</p>
                  <div className="flex items-center space-x-2 text-[#D4AF37] opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="text-sm uppercase tracking-wider">Explore</span>
                    <ArrowRight size={16} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {featuredProducts.length > 0 && (
        <section className="py-24 px-6 md:px-12 bg-[#141414]/30" data-testid="featured-section">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16 space-y-4">
              <div className="flex items-center justify-center space-x-2">
                <Sparkles className="text-[#D4AF37]" size={20} />
                <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Featured Collection</p>
                <Sparkles className="text-[#D4AF37]" size={20} />
              </div>
              <h2 className="text-4xl sm:text-5xl font-light tracking-tighter text-white">
                Signature Pieces
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>

            <div className="text-center mt-12">
              <Link
                to="/products"
                className="inline-block px-10 py-4 bg-transparent text-white border border-white/20 hover:border-white font-medium tracking-wide uppercase text-sm transition-all duration-300"
                data-testid="view-all-button"
              >
                View All Collection
              </Link>
            </div>
          </div>
        </section>
      )}

      <section className="py-24 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-4xl sm:text-5xl font-light tracking-tighter text-white">
            Crafted with Excellence
          </h2>
          <p className="text-lg text-[#A3A3A3] leading-relaxed">
            Each piece in our collection is meticulously crafted by master artisans, 
            using only the finest materials. We believe in creating jewelry that transcends 
            time, pieces that will be cherished for generations.
          </p>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
